import { css, keyframes, } from '@emotion/react';
import rem from './rem';
import remToPxValue from './remToPxValue';
import { bottomShadow, topShadow, } from './shadow';
import theme from './theme';
/** Opacity for disabled state. */
const disabledStateOpacity = 0.3;
/** Apply box sizing to this element and all children. */
const borderBox = css `
  &,
  & * {
    box-sizing: border-box;
  }
`;
/**  Reset styles for HTML button element. */
const buttonReset = css `
  border: none;
  border-radius: 0;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
/** Flex styling with centered content. */
const flexCenter = css `
  display: flex;
  justify-content: center;
  align-items: center;
`;
/** Reset styles for HTML list elements (`ul` or `ol`). */
const listReset = css `
  list-style: none;
  padding: 0;
  margin: 0;
`;
/** Reset all styles. */
const resetAll = css `
  position: static;
  float: none;
  width: auto;
  height: auto;
  clear: both;
  margin: 0;
  border: 0;
  padding: 0;
`;
/** Takes CSS transition properties (`transition-property` attribute) and outputs CSS transition styles. */
const transition = (properties) => (css `
    transition-duration: ${theme.durationDefault};
    transition-property: ${Array.isArray(properties) ? properties.join(',') : properties};
    transition-timing-function: ${theme.easingDefault};
  `);
/** Styles for visible scrollbars. */
const visibleScrollbarStyles = css `
  &::-webkit-scrollbar {
    background: ${theme.colors.contentSecondaryInvert};
    border-radius: ${theme.spacing.base};
    height: ${theme.spacing.base};
    outline: none;
    width: ${theme.spacing.base};
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.contentSecondaryInvert};
    border-radius: ${theme.spacing.base};
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.contentSecondary};
    border-radius: ${theme.spacing.base};
    height: ${theme.spacing.base};
    outline: none;
    width: ${theme.spacing.base};
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.gray600};
    border-radius: ${theme.spacing.base};
    height: ${theme.spacing.base};
    outline: none;
    width: ${theme.spacing.base};
  }
`;
/** Styles for hiding scrollbars. Recommended for touch devices and small screen width. */
const hiddenScrollbarStyles = css `
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE 10+ */

  &::-webkit-scrollbar {
    width: 0;
  }
`;
/**
 * To make for example the heading hierarchy semantically correct or adding icon titles for a11y
 * @see https://www.joshwcomeau.com/snippets/react-components/visually-hidden/
 */
const visuallyHidden = css `
  border: 0;
  clip: rect(0 0 0 0);
  display: inline-block;
  height: 0;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 0;
`;
/** Scales text based on vw. base is target font size (unitless # representing px) at the breakpont (in rems, usually should pass a theme breakpoint var here)*/
export const scalePxToVw = (base, breakpoint) => (`${base / (parseInt(breakpoint, 10) * 16 * 0.01)}vw`);
const skeletonShimmer = keyframes `
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
`;
/** Base styles for skeleton components (including shimmer animation). */
const baseSkeletonStyles = css `
  animation: ${skeletonShimmer} 2s ease infinite;
  background: linear-gradient(
    to right,
    ${theme.colors.gray200},
    ${theme.colors.gray400}
  );
  background-size: 200%;
  border-radius: ${theme.spacing.x3};
`;
const focusedBorderWidth = 2;
/** Focused state styles. */
const focusVisibleStyles = css `
  outline-color: ${theme.colors.borderFocused};
  outline-style: solid;
  outline-width: ${rem(focusedBorderWidth)};
  outline-offset: ${rem(focusedBorderWidth)};
`;
const styleUtils = {
    baseSkeletonStyles,
    borderBox,
    bottomShadow,
    buttonReset,
    disabledStateOpacity,
    flexCenter,
    focusVisibleStyles,
    hiddenScrollbarStyles,
    listReset,
    rem,
    remToPxValue,
    resetAll,
    topShadow,
    transition,
    visibleScrollbarStyles,
    visuallyHidden,
};
export default styleUtils;
